import { useEffect, useState, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { PageHeader, LineChart, BarChart } from '@carrent/ui'
import { api } from '@carrent/api'

const PERIODS = [
  { value: '7',   label: '7 дней' },
  { value: '30',  label: '30 дней' },
  { value: '90',  label: '90 дней' },
  { value: 'all', label: 'Всё время' },
]

const TYPE_COLORS = {
  refuel:      '#f59e0b',
  maintenance: '#ef4444',
  wash:        '#3b82f6',
  other:       '#6b7280',
}

// ── Большой KPI-блок ──────────────────────────────────────────────────────────
function KpiBox({ value, unit, label, color, sub, onClick }) {
  return (
    <div className="card" style={{
      cursor: onClick ? 'pointer' : 'default',
      padding: 'var(--gap-sm) var(--gap)',
    }} onClick={onClick}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{ fontSize: '1.6rem', fontWeight: 800, color: color || 'var(--text)' }}>
          {value ?? '—'}
        </span>
        {unit && <span style={{ fontSize: '0.85rem', color: 'var(--text-muted)' }}>{unit}</span>}
      </div>
      <div className="text-muted" style={{ fontSize: '0.78rem' }}>{label}</div>
      {sub && <div className="text-muted" style={{ fontSize: '0.7rem', marginTop: 2 }}>{sub}</div>}
    </div>
  )
}

// ── Прогресс-бар утилизации ───────────────────────────────────────────────────
function UtilizationBar({ pct }) {
  if (pct === null || pct === undefined) {
    return <div className="text-muted" style={{ fontSize: '0.82rem' }}>—</div>
  }
  const color = pct < 30 ? 'var(--danger)'
    : pct < 60 ? 'var(--warning)' : 'var(--success)'

  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
        <span style={{ fontSize: '0.82rem', fontWeight: 600 }}>Утилизация парка</span>
        <span style={{ fontSize: '1rem', fontWeight: 800, color }}>
          {pct}%
        </span>
      </div>
      <div style={{ height: 8, background: 'var(--bg)', borderRadius: 99, overflow: 'hidden' }}>
        <div style={{
          width: `${pct}%`, height: '100%', background: color,
          transition: 'width 0.5s ease',
        }} />
      </div>
      <p className="text-muted" style={{ fontSize: '0.7rem', marginTop: 4 }}>
        Сколько % времени машины проводят в поездках
      </p>
    </div>
  )
}

// Линейный график и гистограмма переехали в @carrent/ui:
// их использует и эта страница, и информационная панель.

// ── Распределение рейтингов водителей ─────────────────────────────────────────
function RatingDistribution({ buckets, total }) {
  const items = [
    { key: 'excellent', label: 'Отличные (80+)', color: '#22c55e' },
    { key: 'good',      label: 'Хорошие (60-79)', color: '#84cc16' },
    { key: 'warning',   label: 'Средние (40-59)', color: '#f59e0b' },
    { key: 'danger',    label: 'Плохие (<40)',    color: '#ef4444' },
  ]
  return (
    <div className="flex-col" style={{ gap: 8 }}>
      {items.map(it => {
        const count = buckets[it.key] || 0
        const pct = total > 0 ? (count / total * 100) : 0
        return (
          <div key={it.key}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 3 }}>
              <span style={{ fontSize: '0.82rem' }}>{it.label}</span>
              <span style={{ fontSize: '0.82rem', fontWeight: 700, color: it.color }}>
                {count} <span className="text-muted" style={{ fontWeight: 400 }}>({pct.toFixed(0)}%)</span>
              </span>
            </div>
            <div style={{ height: 6, background: 'var(--bg)', borderRadius: 99 }}>
              <div style={{ width: `${pct}%`, height: '100%', background: it.color,
                borderRadius: 99, transition: 'width 0.5s' }} />
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ── Главный компонент ────────────────────────────────────────────────────────
export default function AdminAnalyticsPage() {
  const navigate = useNavigate()
  const [data, setData]       = useState(null)
  const [loading, setLoading] = useState(true)
  const [period, setPeriod]   = useState('30')

  const load = useCallback(() => {
    setLoading(true)
    api.get('/admin/analytics', { params: { period } })
      .then(r => setData(r.data?.data))
      .catch(() => {})
      .finally(() => setLoading(false))
  }, [period])

  useEffect(load, [load])

  if (loading) return (
    <div>
      <PageHeader title="Аналитика" />
      <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
        <div className="spinner" style={{ width: 40, height: 40 }} />
      </div>
    </div>
  )
  if (!data) return <div><PageHeader title="Нет данных" /></div>

  const { fleet, finance, drivers, trips, support, managers } = data
  const sortedExpenses = Object.entries(finance.expenses_by_type)
    .filter(([, v]) => v > 0)
    .sort(([, a], [, b]) => b - a)

  const HOURS = Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0'))
  const WEEKDAYS = ['Пн', 'Вт', 'Ср', 'Чт', 'Пт', 'Сб', 'Вс']

  return (
    <div style={{ paddingBottom: 'var(--gap-xl)' }}>
      <PageHeader title="📊 Аналитика" />

      <div className="p-page flex-col" style={{ gap: 'var(--gap)' }}>

        {/* Переключатель периода */}
        <div style={{ display: 'flex', gap: 6, overflowX: 'auto', scrollbarWidth: 'none' }}>
          {PERIODS.map(p => (
            <button key={p.value}
              className={`btn ${period === p.value ? 'btn--primary' : 'btn--outline'}`}
              style={{ flexShrink: 0, padding: '6px 14px', fontSize: '0.82rem' }}
              onClick={() => setPeriod(p.value)}>
              {p.label}
            </button>
          ))}
        </div>

        {/* ═══════ 1. ПАРК ═══════ */}
        <p className="section-title">🚗 Автопарк</p>

        <div className="card">
          <UtilizationBar pct={fleet.utilization_pct} />
        </div>

        <div className="stat-grid">
          <KpiBox value={fleet.total}        label="Всего машин" />
          <KpiBox value={fleet.in_use}       label="В поездке" color="var(--accent)" />
          <KpiBox value={fleet.available}    label="Доступно" color="var(--success)" />
          <KpiBox value={fleet.maintenance + fleet.inspection_required + fleet.blocked}
                  label="Не работают"
                  color={(fleet.maintenance + fleet.inspection_required + fleet.blocked) > 0 ? 'var(--warning)' : 'var(--text-muted)'}
                  sub={`ТО: ${fleet.maintenance} · осмотр: ${fleet.inspection_required} · блок: ${fleet.blocked}`} />
        </div>

        {/* ═══════ 2. ПОЕЗДКИ ═══════ */}
        <p className="section-title">📋 Поездки за период</p>

        <div className="stat-grid">
          <KpiBox value={trips.total} label="Завершено" />
          <KpiBox value={fleet.avg_distance_per_trip} unit=" км" label="Ср. дистанция" />
          <KpiBox value={fleet.avg_duration_min ? `${Math.floor(fleet.avg_duration_min/60)}ч ${fleet.avg_duration_min%60}м` : '—'}
                  label="Ср. длительность" />
          <KpiBox value={trips.damage_pct} unit="%" label="С повреждениями"
                  color={trips.damage_pct > 10 ? 'var(--danger)' : trips.damage_pct > 5 ? 'var(--warning)' : 'var(--success)'}
                  sub={`${trips.with_damage} из ${trips.total}`} />
        </div>

        {/* Часы пик */}
        {trips.total > 0 && (
          <>
            <div className="card">
              <p style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 8 }}>
                Поездки по часам суток
              </p>
              <BarChart values={trips.by_hour}
                labels={HOURS.map((h, i) => i % 3 === 0 ? h : '')}
                color="#3b82f6" />
            </div>

            <div className="card">
              <p style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 8 }}>
                По дням недели
              </p>
              <BarChart values={trips.by_weekday} labels={WEEKDAYS} color="#8b5cf6" />
            </div>
          </>
        )}

        {trips.pending_review > 0 && (
          <div className="card" style={{
            background: 'rgba(245,158,11,0.08)',
            borderColor: 'rgba(245,158,11,0.3)',
            cursor: 'pointer',
          }} onClick={() => navigate('/admin/trips?status=finished_by_user')}>
            <p style={{ fontWeight: 600, color: 'var(--warning)' }}>
              ⏳ {trips.pending_review} поездок ждут подтверждения →
            </p>
          </div>
        )}

        {/* ═══════ 3. ФИНАНСЫ ═══════ */}
        <p className="section-title">💰 Финансы</p>

        <div className="stat-grid">
          <KpiBox value={finance.total_expenses.toLocaleString()} unit=" ₽"
                  label="Расходы" color="var(--warning)" />
          <KpiBox value={finance.cost_per_km} unit=" ₽" label="₽ за км"
                  sub={`${finance.total_distance.toLocaleString()} км всего`} />
        </div>

        {/* Структура расходов */}
        {sortedExpenses.length > 0 && (
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <p style={{ fontSize: '0.85rem', fontWeight: 600,
              padding: 'var(--gap-sm) var(--gap)',
              borderBottom: '1px solid var(--border)' }}>
              Структура расходов
            </p>
            {sortedExpenses.map(([type, amount]) => {
              const pct = (amount / finance.total_expenses * 100)
              return (
                <div key={type} style={{ padding: '10px var(--gap)',
                  borderBottom: '1px solid var(--border)' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 4 }}>
                    <span style={{ fontSize: '0.85rem' }}>
                      {finance.expenses_labels[type]}
                    </span>
                    <span style={{ fontWeight: 700, color: TYPE_COLORS[type] }}>
                      {amount.toLocaleString()} ₽
                      <span className="text-muted" style={{ fontWeight: 400, marginLeft: 6, fontSize: '0.75rem' }}>
                        {pct.toFixed(0)}%
                      </span>
                    </span>
                  </div>
                  <div style={{ height: 4, background: 'var(--bg)', borderRadius: 99 }}>
                    <div style={{ width: `${pct}%`, height: '100%',
                      background: TYPE_COLORS[type], borderRadius: 99,
                      transition: 'width 0.5s' }} />
                  </div>
                </div>
              )
            })}
          </div>
        )}

        {/* График расходов по дням */}
        {finance.daily.length > 1 && (
          <div className="card">
            <p style={{ fontSize: '0.82rem', fontWeight: 600, marginBottom: 8 }}>
              Динамика расходов по дням
            </p>
            <LineChart data={finance.daily} valueKey="expenses" color="#f59e0b" />
          </div>
        )}

        {/* Топ дорогих машин */}
        {finance.top_expensive_cars.length > 0 && (
          <>
            <p className="section-title">💸 Топ-5 дорогих машин (по ₽/км)</p>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {finance.top_expensive_cars.map((c, i) => (
                <div key={c.car_id} style={{ padding: '10px var(--gap)',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/cars/${c.car_id}/finance`)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: '1rem',
                      color: i === 0 ? 'var(--danger)' : i === 1 ? 'var(--warning)' : 'var(--text-muted)',
                      minWidth: 22 }}>
                      #{i+1}
                    </span>
                    <div>
                      <p style={{ fontSize: '0.88rem', fontWeight: 600 }}>
                        {c.brand} {c.model}
                      </p>
                      <p className="text-muted" style={{ fontSize: '0.72rem' }}>
                        {c.plate} · {c.distance.toLocaleString()} км · {c.trips} поездок
                      </p>
                    </div>
                  </div>
                  <div style={{ textAlign: 'right' }}>
                    <p style={{ fontWeight: 700, color: 'var(--warning)' }}>
                      {c.cost_per_km} ₽/км
                    </p>
                    <p className="text-muted" style={{ fontSize: '0.7rem' }}>
                      {c.expenses.toLocaleString()} ₽
                    </p>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ═══════ 4. ВОДИТЕЛИ ═══════ */}
        <p className="section-title">👥 Водители</p>

        <div className="stat-grid">
          <KpiBox value={drivers.approved} label="Одобрено" color="var(--success)" />
          <KpiBox value={drivers.active}   label="Активных за период"
                  color="var(--accent)"
                  sub={`${drivers.approved > 0 ? Math.round(drivers.active/drivers.approved*100) : 0}% от одобренных`} />
          <KpiBox value={drivers.pending}  label="На проверке"
                  color={drivers.pending > 0 ? 'var(--warning)' : 'var(--text-muted)'}
                  onClick={() => navigate('/admin/users?status=pending')} />
          <KpiBox value={drivers.avg_rating} label="Средний рейтинг"
                  color={drivers.avg_rating >= 75 ? 'var(--success)' : drivers.avg_rating >= 50 ? 'var(--warning)' : 'var(--danger)'} />
        </div>

        {/* Распределение рейтингов */}
        {drivers.approved > 0 && (
          <div className="card">
            <p style={{ fontSize: '0.85rem', fontWeight: 600, marginBottom: 10 }}>
              Распределение по рейтингам
            </p>
            <RatingDistribution buckets={drivers.rating_buckets} total={drivers.approved} />
          </div>
        )}

        {/* Топ по поездкам */}
        {drivers.top_by_trips.length > 0 && (
          <>
            <p className="section-title">🏆 Топ-5 по поездкам</p>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {drivers.top_by_trips.map((d, i) => (
                <div key={d.user_id} style={{ padding: '10px var(--gap)',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between', alignItems: 'center',
                  cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/users/${d.user_id}`)}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontWeight: 700, fontSize: '1rem',
                      color: i === 0 ? '#f59e0b' : 'var(--text-muted)',
                      minWidth: 22 }}>
                      #{i+1}
                    </span>
                    <div>
                      <p style={{ fontSize: '0.88rem', fontWeight: 600 }}>{d.full_name}</p>
                      <p className="text-muted" style={{ fontSize: '0.72rem' }}>
                        {d.distance.toLocaleString()} км
                        {d.damage > 0 && <span style={{ color: 'var(--danger)' }}> · ⚠️ {d.damage}</span>}
                      </p>
                    </div>
                  </div>
                  <span style={{ fontWeight: 700 }}>{d.trips} 🚗</span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* Водители с повреждениями */}
        {drivers.with_damage.length > 0 && (
          <>
            <p className="section-title" style={{ color: 'var(--danger)' }}>
              ⚠️ Водители с повреждениями
            </p>
            <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
              {drivers.with_damage.map(d => (
                <div key={d.user_id} style={{ padding: '8px var(--gap)',
                  borderBottom: '1px solid var(--border)',
                  display: 'flex', justifyContent: 'space-between',
                  cursor: 'pointer' }}
                  onClick={() => navigate(`/admin/users/${d.user_id}`)}>
                  <span style={{ fontSize: '0.85rem' }}>{d.full_name}</span>
                  <span style={{ color: 'var(--danger)', fontWeight: 700, fontSize: '0.85rem' }}>
                    {d.damage} из {d.trips}
                  </span>
                </div>
              ))}
            </div>
          </>
        )}

        {/* ═══════ 5. ПОДДЕРЖКА ═══════ */}
        <p className="section-title">💬 Поддержка</p>

        <div className="stat-grid">
          <KpiBox value={support.total_period} label="Тикетов за период" />
          <KpiBox value={support.active}   label="Активных"
                  color={support.active > 0 ? 'var(--accent)' : 'var(--text-muted)'}
                  onClick={() => navigate('/admin/tickets')} />
          <KpiBox value={support.urgent}   label="Срочных"
                  color={support.urgent > 0 ? 'var(--danger)' : 'var(--text-muted)'}
                  onClick={() => navigate('/admin/tickets?priority=urgent')} />
          <KpiBox value={support.avg_resolution_hours
                    ? `${support.avg_resolution_hours}ч`
                    : '—'}
                  label="Ср. время решения" />
        </div>

        {/* ═══════ 6. МЕНЕДЖЕРЫ ═══════ */}
        {managers && (
          <>
            <p className="section-title">🔧 Производительность менеджеров</p>

            <div className="stat-grid">
              <KpiBox value={managers.summary.active_managers}
                      label="Активных" sub={`из ${managers.summary.total_managers}`} />
              <KpiBox value={managers.summary.trips_confirmed}
                      label="Подтверждено поездок"
                      color={managers.summary.trips_confirmed > 0 ? 'var(--accent)' : 'var(--text-muted)'} />
              <KpiBox value={managers.summary.users_reviewed}
                      label="Проверено водителей"
                      color={managers.summary.users_reviewed > 0 ? 'var(--accent)' : 'var(--text-muted)'} />
              <KpiBox value={managers.summary.tickets_handled}
                      label="Закрыто тикетов"
                      color={managers.summary.tickets_handled > 0 ? 'var(--success)' : 'var(--text-muted)'} />
            </div>

            {/* Средние времена реакции */}
            {(managers.summary.avg_confirm_hours ||
              managers.summary.avg_review_hours ||
              managers.summary.avg_ticket_hours) && (
              <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
                {[
                  ['🚗 Подтверждение поездки',     managers.summary.avg_confirm_hours],
                  ['👥 Решение по верификации',     managers.summary.avg_review_hours],
                  ['💬 Закрытие тикета',            managers.summary.avg_ticket_hours],
                ].map(([label, hours]) => hours !== null && (
                  <div key={label} style={{
                    display: 'flex', justifyContent: 'space-between',
                    padding: '10px var(--gap)',
                    borderBottom: '1px solid var(--border)',
                  }}>
                    <span className="text-muted" style={{ fontSize: '0.85rem' }}>{label}</span>
                    <span style={{ fontWeight: 700, fontSize: '0.9rem' }}>
                      ср. {hours < 1
                        ? `${Math.round(hours * 60)} мин`
                        : `${hours} ч`}
                    </span>
                  </div>
                ))}
              </div>
            )}

            {/* Карточки по каждому менеджеру */}
            {managers.items.length > 0 && (
              <>
                <p className="section-title">Личная активность</p>
                {managers.items.map(m => (
                  <ManagerCard key={m.user_id} m={m}
                    onClick={() => navigate(`/admin/users/${m.user_id}`)} />
                ))}
              </>
            )}
          </>
        )}

      </div>
    </div>
  )
}

// ── Карточка менеджера ───────────────────────────────────────────────────────
function ManagerCard({ m, onClick }) {
  const isInactive = m.total_actions === 0
  const roleIcon = m.role === 'admin' ? '👑' : '🔧'
  const roleLabel = m.role === 'admin' ? 'Администратор' : 'Менеджер'

  return (
    <div className="card" style={{
      cursor: 'pointer', padding: 0, overflow: 'hidden',
      opacity: isInactive ? 0.6 : 1,
    }} onClick={onClick}>
      {/* Шапка */}
      <div style={{
        display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        padding: '10px var(--gap)',
        borderBottom: m.total_actions > 0 ? '1px solid var(--border)' : 'none',
      }}>
        <div>
          <p style={{ fontWeight: 700, fontSize: '0.95rem' }}>
            {roleIcon} {m.full_name}
          </p>
          <p className="text-muted" style={{ fontSize: '0.75rem', marginTop: 2 }}>
            {roleLabel} · {m.phone}
          </p>
        </div>
        {isInactive ? (
          <span className="text-muted" style={{ fontSize: '0.78rem' }}>
            нет активности
          </span>
        ) : (
          <div style={{ textAlign: 'right' }}>
            <p style={{ fontWeight: 800, fontSize: '1.15rem', color: 'var(--accent)' }}>
              {m.total_actions}
            </p>
            <p className="text-muted" style={{ fontSize: '0.7rem' }}>
              действий
            </p>
          </div>
        )}
      </div>

      {/* Метрики */}
      {m.total_actions > 0 && (
        <div style={{
          display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)',
          padding: '8px var(--gap-sm)', gap: 4,
        }}>
          {/* Поездки */}
          <div style={{ textAlign: 'center', padding: '4px' }}>
            <p style={{ fontWeight: 700, fontSize: '1rem' }}>
              {m.trips_confirmed}
            </p>
            <p className="text-muted" style={{ fontSize: '0.7rem' }}>
              🚗 поездок
            </p>
            {m.avg_confirm_hours !== null && (
              <p style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 1 }}>
                ср. {m.avg_confirm_hours < 1
                  ? `${Math.round(m.avg_confirm_hours*60)}м`
                  : `${m.avg_confirm_hours}ч`}
              </p>
            )}
          </div>

          {/* Водители */}
          <div style={{ textAlign: 'center', padding: '4px',
            borderLeft: '1px solid var(--border)', borderRight: '1px solid var(--border)' }}>
            <p style={{ fontWeight: 700, fontSize: '1rem' }}>
              {m.users_reviewed}
            </p>
            <p className="text-muted" style={{ fontSize: '0.7rem' }}>
              👥 проверок
            </p>
            {m.users_reviewed > 0 && (
              <p style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 1 }}>
                ✓{m.users_approved} ✕{m.users_rejected}
              </p>
            )}
          </div>

          {/* Тикеты */}
          <div style={{ textAlign: 'center', padding: '4px' }}>
            <p style={{ fontWeight: 700, fontSize: '1rem' }}>
              {m.tickets_resolved}
            </p>
            <p className="text-muted" style={{ fontSize: '0.7rem' }}>
              💬 тикетов
            </p>
            {m.messages_sent > 0 && (
              <p style={{ fontSize: '0.65rem', color: 'var(--text-muted)', marginTop: 1 }}>
                {m.messages_sent} сообщ.
              </p>
            )}
          </div>
        </div>
      )}
    </div>
  )
}
